<template>
  <div class="map-container" ref="mapDivRef"></div>
  <div v-if="loadingButton || heatmapLoading" class="map-loading">
    <i class="fa fa-spinner fa-spin me-1"></i> Loading
  </div>
  <div class="map-toggle-buttons">
    <div class="filter-options">
      <div class="filter-label">Bands:</div>
      <button
        v-for="option in bandOptions"
        :key="option.value"
        class="btn toggle-btn"
        :class="[
          {
            'active-option': activeBands.includes(option.value),
            'disabled-state':
              heatmapLoading && !activeBands.includes(option.value),
          },
        ]"
        @click="heatmapLoading ? null : setActiveBands(option.value)"
      >
        {{ option.label }}
      </button>
    </div>
    <div class="filter-options">
      <div class="filter-label">Heatmap:</div>
      <button
        v-for="option in heatmapOptions"
        :key="option.value"
        class="btn toggle-btn"
        :class="[
          {
            'active-option': activeHeatmap === option.value,
            'disabled-state': heatmapLoading && activeHeatmap != option.value,
          },
        ]"
        @click="heatmapLoading ? null : setActiveHeatmap(option.value)"
      >
        {{ option.label }}
      </button>
    </div>
    <div class="filter-options">
      <div class="filter-label">Height:</div>
      <button
        v-for="option in heightOptions"
        :key="option.value"
        class="btn toggle-btn"
        :class="[
          {
            'active-option': activeHeight === option.value,
            'disabled-state': heatmapLoading && activeHeight != option.value,
          },
        ]"
        @click="heatmapLoading ? null : setActiveHeight(option.value)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
  <div class="reset-container">
    <button
      class="btn lock-zoom-btn"
      data-cy="reset-button"
      @click="resetAllValues"
      v-if="resetAll"
    >
      Reset
    </button>
    <button
      class="btn lock-zoom-btn"
      :class="{ 'with-right-spacing': checkResultExist }"
      @click="handleHeatmap"
    >
      {{ enableHeatmap ? "Enable" : "Disable" }} Image Tile
    </button>

    <!-- <button
      class="btn lock-zoom-btn"
      @click="toggleLockZoom"
      v-if="showLockZoomButton && checkResultExist"
      :class="{ 'with-right-spacing': showLockZoomButton && checkResultExist }"
    >
      {{ lockZoom ? "Unlock" : "Lock" }} Map / Zoom
    </button> -->

    <!-- Legend Image -->
    <img
      v-if="legendUrl"
      :src="legendUrl"
      alt="Heatmap Legend"
      class="legend-image with-top-spacing"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { createLabelOverlay } from "@/utils/LabelOverlay";
import { createHoverMarkerOverlay } from "@/utils/HoverMarkerOverlay.js";

export default {
  name: "GoogleMap",
  props: {
    operatorKey: String,
    loadMapRadius: Boolean,
    hoverMarkerData: Object,
    baseNodeList: Object,
    remoteAddress: String,
    remoteLatLng: Object,
    sectorResults: Object,
    activeSector: String,
    heatmapData: Object,
    multiSector: Boolean,
    clearRemoteNode: Boolean,
    loadingButton: Boolean,
  },
  emits: [
    "map-path-hover",
    "update-lat-lng",
    "update-bn-radius",
    "reset-all",
    "manage-base-node",
    "update-base-node",
    "update-results-modal",
    "update-active-height",
  ],
  setup(props, { emit }) {
    const mapDivRef = ref(null);
    const mapType = ref("hybrid");
    let map = null;
    let route = null;
    let borderLine = null;
    let labelMarker = null;
    let kmlOverlay = null;
    let activeBands = ref(["3", "5", "6"]);
    let activeHeatmap = ref(null);
    let activeHeight = ref(null);
    let previousHeatmap = ref(null);
    let previousHeight = ref(null);
    let activeRadius = ref(null);
    let legendUrl = ref(null);
    let overlayUrl = ref(null);
    let multiSectorFlag = ref(false);
    let loadingButton = ref(false);
    let heatmapLoading = ref(false);
    let heatmapRemoved = ref(false);
    let heatmapImageLoaded = ref(false);
    let bandsValueChanged = ref(false);
    let sectorValueChanged = ref(false);
    let clusterClicked = ref(false);
    let mapFiltering = ref(false);
    let lockZoom = ref(false);
    let lockMapUpdate = ref(false);
    let resetAll = ref(false);
    let mapClickEvent = ref(false);
    let mapDragEvent = ref(false);
    let addressChanged = ref(false);
    let latlngChanged = ref(false);
    let zoomChanged = ref(false);
    let modalHeatmapExist = ref(false);
    let resultsModalRemoved = ref(false);
    let activeHeatmapRemoved = ref(false);
    let activeHeightRemoved = ref(false);
    let checkAzimuthExist = ref(false);
    let checkResultExist = ref(false);
    let clearAllMarkers = ref(true);
    let azimuthPolygonClicked = ref(false);
    let enableHeatmap = ref(true);
    let hoverMarkerExist = ref(false);
    let newLocation = null;
    let remoteOldLocation = null;
    let remoteLocation = null;
    let bnMarkerIcon = null;
    let rnMarkerIcon = null;
    let hoverMarkerIcon = null;
    let lastHoverLatLng = null;
    let baseMarker = null;
    let remoteMarker = null;
    let newRemoteMarker = null;
    let infoWindow = null;
    let bnEncodedSVG = null;
    let rnEncodedSVG = null;
    let mapClickListener = null;
    let radiusCircle = null;
    let radiusRectangle = null;
    let markerClusterer = null;
    let selectedRNList = null;
    let selectedRN = null;
    let selectedBN = null;
    let labelOverlay = null;
    let setLatMidpoint = null;
    let setLngMidpoint = null;
    let usedNodeforMidpoint = null;
    let activeSector = null;
    let activeBaseNode = null;
    let activeBNIndex = -1;
    let labelBackground = "#0084b4";
    let customOverlays = [];
    let sectorResults = [];
    let baseNodes = [];
    let baseNodeList = [];
    let remoteNodeList = [];
    let baseNodeMarkers = [];
    let plottedBNMarkers = [];
    let polygonArray = [];
    let polylineArray = [];
    let labelMarkersArray = [];
    let heatmapList = [];
    let heatmapLegends = [];
    let heatmapSuffixes = [];
    let heatmapFilterData = [];
    let heatmapArray = [];
    let sectorsArray = {};
    let bandOptions = [
      { label: "3 GHz", value: "3" },
      { label: "5 GHz", value: "5" },
      { label: "6 GHz", value: "6" },
    ];
    let heatmapOptions = [
      { label: "Confidence", value: "confidence" },
      { label: "Path Loss", value: "path_loss" },
      { label: "Speed", value: "link_speed" },
    ];
    let heightOptions = [
      { label: "3 m / 10 m", value: "3_10" },
      { label: "2 m / 7 m", value: "2_7" },
      { label: "1 m / 5 m", value: "1_5" },
    ];

    const toggleLockZoom = () => {
      lockZoom.value = !lockZoom.value;
      handleMapLockZoom();
    };

    const handleMapLockZoom = () => {
      if (map) {
        if (lockZoom.value) {
          map.setOptions({
            scrollwheel: false,
            disableDoubleClickZoom: true,
            draggable: false,
          });
        } else {
          map.setOptions({
            scrollwheel: true,
            disableDoubleClickZoom: false,
            draggable: true,
          });
        }
      }
    };

    const setActiveBands = (band) => {
      if (activeBands.value.includes(band)) {
        // Remove Band if already selected
        activeBands.value = activeBands.value.filter((b) => b !== band);
      } else {
        // Add Band if not selected
        activeBands.value.push(band);
      }
      updateMapWithBaseNodes();
      if (checkResultExist.value) {
        mapFiltering.value = true;
        bandsValueChanged.value = true;
        updateMap();
      }
    };

    const setActiveHeatmap = (heatmap) => {
      if (activeHeatmap.value === heatmap) {
        // Deactivate if the same heatmap is clicked again
        activeHeatmap.value = null;
        activeHeatmapRemoved.value = true;
        removeHeatmap();
      } else {
        activeHeatmapRemoved.value = false;
        // Save previous values before updating
        previousHeatmap.value = heatmap;
        activeHeatmap.value = heatmap;

        if (!activeHeight.value) {
          activeHeight.value = previousHeight.value; // Restore previous height if missing
        }
        heatmapImageLoaded.value = false;
        updateHeatmap();
      }
    };

    const setActiveHeight = (height) => {
      if (activeHeight.value === height) {
        // Deactivate if the same height is clicked again
        activeHeight.value = null;
        activeHeightRemoved.value = true;
        removeHeatmap();
      } else {
        activeHeightRemoved.value = false;
        // Save previous values before updating
        previousHeight.value = height;
        activeHeight.value = height;
        if (!activeHeatmap.value) {
          activeHeatmap.value = previousHeatmap.value; // Default heatmap if none selected
        }
        heatmapImageLoaded.value = false;
        updateHeatmap();
      }
      if (checkResultExist.value) {
        emit("update-active-height", {
          base_node: activeBaseNode,
          remote_data: selectedRN,
          remote_list: selectedRNList,
          height: activeHeight.value,
          remove_result: resultsModalRemoved.value,
        });
      }
    };

    const handleHoverMarker = (coords) => {
      if (!coords) {
        hoverMarkerExist.value = false;
        hoverMarkerIcon.setVisible(false);
        lastHoverLatLng = null;
        return;
      }

      const adjustedCoords = coords;

      // Prevent redundant DOM updates
      const latLngStr = `${adjustedCoords.lat.toFixed(
        7
      )}|${adjustedCoords.lng.toFixed(7)}`;
      if (lastHoverLatLng === latLngStr) return;

      lastHoverLatLng = latLngStr;
      hoverMarkerIcon.setPosition(adjustedCoords);
      hoverMarkerIcon.setVisible(true);
    };

    const handleMapWithSector = () => {
      let activeBN = baseNodes.find((bn) => bn.sector == activeSector) ?? null;
      if (checkResultExist.value && activeBN) {
        activeBaseNode = activeBN;
        sectorValueChanged.value = true;
        updateMap();
      }
    };

    const handleHeatmapFromModal = (data) => {
      /* if (!activeHeatmap.value && !activeHeatmapRemoved.value) {
        activeHeatmap.value = "confidence";
        previousHeatmap.value = "confidence";
      } */
      if (!activeHeightRemoved.value) {
        let heightValue = data.activeHeight;
        activeHeight.value = heightValue;
        previousHeight.value = heightValue;
      }
      if (data.base_node) {
        activeBaseNode = data.base_node ?? [];
      }
      if (data.heatmap_list) {
        heatmapList = data.heatmap_list ?? [];
      }
      heatmapImageLoaded.value = false;
      updateHeatmap();
    };

    const updateHeatmap = () => {
      if (!map) return;

      // Remove existing heatmap if any
      removeHeatmap();

      if (activeHeatmap.value && activeHeight.value && heatmapList.length > 0) {
        heatmapFilterData = filteredHeatmapData(
          activeHeatmap.value,
          activeHeight.value
        );
        heatmapRemoved.value = false;
        sectorValueChanged.value = false;
        emit("manage-base-node", {
          results_exist: checkResultExist.value,
          heatmap_exist: true,
        });
        updateMapWithBaseNodes();
        const heatmapKmlUrl = heatmapFilterData.kml_url;

        fetchKMLBounds(heatmapKmlUrl);
      }
    };

    const filteredHeatmapData = (heatmap, height) => {
      // heatmap = confidence height = 3_10

      let heightData = height.split("_").map(Number);

      let filteredByHeight = heatmapList.filter(
        (item) =>
          item.height.above_roof === heightData[0] &&
          item.height.above_open === heightData[1]
      );

      if (filteredByHeight.length === 0) return null; // Check if array is empty

      let filteredData = "";

      if (filteredByHeight.length > 0) {
        const { base_url, kml, overlay, legend } = filteredByHeight[0];
        const hasSuffixes = Object.keys(heatmapSuffixes).length > 0;

        const getUrl = (suffixObj, fallbackObj) =>
          hasSuffixes
            ? suffixObj?.aiml_output?.[heatmap] || ""
            : fallbackObj?.aiml_output?.[heatmap] || "";

        const kmlUrl = getUrl(heatmapSuffixes.kml, kml);
        const overlayImage = getUrl(heatmapSuffixes.overlay, overlay);
        const legendImage = getUrl(heatmapSuffixes.legend, legend);

        filteredData = {
          kml_url: base_url + kmlUrl,
          overlay_image: base_url + overlayImage,
          legend_image: legendImage,
        };
      }

      return filteredData;
    };

    const fetchKMLBounds = async (kmlUrl) => {
      try {
        heatmapLoading.value = true;
        const response = await fetch(kmlUrl);
        const kmlText = await response.text();

        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(kmlText, "application/xml");

        const bounds = extractBoundsFromKML(kmlDoc);

        if (bounds) {
          //console.log("Extracted Bounds:", bounds);
          if (
            activeBaseNode.operator == "Sail Internet" &&
            !enableHeatmap.value
          ) {
            addImageTileOverlayToMap(bounds);
          } else if (enableHeatmap.value) {
            addHeatmapOverlayToMap(bounds);
          } else {
            heatmapLoading.value = false;
            alert(
              "No active image tiles exist for this operator on the Google Map."
            );
          }
        } else {
          heatmapLoading.value = false;
          console.warn("No bounds found in the KML file.");
        }
      } catch (error) {
        heatmapLoading.value = false;
        console.error("Error fetching KML data:", error);
      }
    };

    const extractBoundsFromKML = (kmlDoc) => {
      const latLonBox = kmlDoc.getElementsByTagName("LatLonBox")[0];

      if (!latLonBox) return null;

      const getValue = (tagName) => {
        const element = latLonBox.getElementsByTagName(tagName)[0];
        return element ? parseFloat(element.textContent) : null;
      };

      return {
        north: getValue("north"),
        south: getValue("south"),
        east: getValue("east"),
        west: getValue("west"),
      };
    };

    // Function to create and track overlay load time
    const addHeatmapOverlayToMap = (bounds) => {
      const img = new Image();

      overlayUrl.value = heatmapFilterData.overlay_image;

      img.src = overlayUrl.value;

      img.onload = () => {
        // Remove the existing overlay if it exists
        if (kmlOverlay) {
          kmlOverlay.setMap(null);
          google.maps.event.clearListeners(kmlOverlay, "click");
          kmlOverlay = null;
        }
        // Now that the image is loaded, add the overlay to the map
        kmlOverlay = new google.maps.GroundOverlay(overlayUrl.value, bounds);
        kmlOverlay.setMap(map); // Add overlay to map
        // Set legend
        legendUrl.value = heatmapFilterData?.legend_image || "";
        // Add Click Listener
        mapClickListener = kmlOverlay.addListener("click", (event) => {
          mapClickHandler(event.latLng);
        });
        heatmapLoading.value = false;
        heatmapImageLoaded.value = true;
      };

      img.onerror = () => {
        heatmapLoading.value = false;
        heatmapImageLoaded.value = false;
        console.error("Error loading overlay image.");
      };

      // Clear previous overlays and insert new one
      if (map.overlayMapTypes.getLength() > 0) {
        map.overlayMapTypes.clear();
      }
    };

    const addImageTileOverlayToMap = (bounds) => {
      if (customOverlays) {
        customOverlays.forEach((overlay) => overlay.setMap(null));
        customOverlays = [];
      }

      let projectId =
        process.env.VUE_APP_PLATFORM === "development"
          ? "tw-network-planning-dev"
          : "tw-network-planning";

      let heightData = activeHeight.value?.split("_").map(Number);
      if (!heightData || !activeBaseNode) return;
      let operatorName = activeBaseNode.operator.replace(/ /g, "_");
      let serialNumber = activeBaseNode.serial;

      let aboveOpen = heightData[1];
      let aboveRoof = heightData[0];
      let modelName = activeHeatmap.value;
      let baseZoom = 13;

      let TILE_URL = (x, y) =>
        `https://storage.googleapis.com/${projectId}/heatmaps/${operatorName}/${serialNumber}/Heights_${aboveOpen}_0_${aboveRoof}_0/${modelName}/tiles_${baseZoom}/kmz/${x}_${y}_${baseZoom}_overlay.png`;

      const mapBounds = bounds
        ? new google.maps.LatLngBounds(
            new google.maps.LatLng(bounds.south, bounds.west),
            new google.maps.LatLng(bounds.north, bounds.east)
          )
        : map.getBounds();
      const { xRange, yRange } = getTileRangeFromBounds(mapBounds, baseZoom);

      for (let x = xRange[0] - 1; x <= xRange[1] + 1; x++) {
        for (let y = yRange[0] - 1; y <= yRange[1] + 1; y++) {
          const tileBounds = getTileBounds(x, y, baseZoom);
          const imageUrl = TILE_URL(x, y);

          const img = new Image();
          img.src = imageUrl;

          img.onload = () => {
            const overlay = new google.maps.OverlayView();

            overlay.onAdd = function () {
              const div = document.createElement("div");
              div.style.border = "none";
              div.style.position = "absolute";

              const innerImg = document.createElement("img");
              innerImg.src = imageUrl;
              innerImg.style.width = "100%";
              innerImg.style.height = "100%";
              innerImg.style.opacity = "1";
              innerImg.style.position = "absolute";

              div.appendChild(innerImg);
              overlay.div = div;

              const panes = this.getPanes();
              panes.overlayLayer.appendChild(div);
            };

            overlay.draw = function () {
              const overlayProjection = this.getProjection();
              const sw = overlayProjection.fromLatLngToDivPixel(
                tileBounds.getSouthWest()
              );
              const ne = overlayProjection.fromLatLngToDivPixel(
                tileBounds.getNorthEast()
              );

              const div = this.div;
              if (div) {
                div.style.left = sw.x + "px";
                div.style.top = ne.y + "px";
                div.style.width = ne.x - sw.x + "px";
                div.style.height = sw.y - ne.y + "px";
              }
            };

            overlay.onRemove = function () {
              if (overlay.div && overlay.div.parentNode) {
                overlay.div.parentNode.removeChild(overlay.div);
                overlay.div = null;
              }
            };

            overlay.setMap(map);
            customOverlays.push(overlay);
            legendUrl.value = heatmapFilterData?.legend_image || "";
            heatmapLoading.value = false;
          };

          img.onerror = () => {
            heatmapLoading.value = false;
          };
        }
      }
    };

    const getTileBounds = (x, y, zoom = 13) => {
      const tile2latLng = (x, y, zoom) => {
        const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, zoom);
        const lat =
          (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
        const lng = (x / Math.pow(2, zoom)) * 360 - 180;
        return { lat, lng };
      };

      const nw = tile2latLng(x, y, zoom);
      const se = tile2latLng(x + 1, y + 1, zoom);
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(se.lat, nw.lng),
        new google.maps.LatLng(nw.lat, se.lng)
      );
    };

    const getTileRangeFromBounds = (bounds, zoom = 13) => {
      const latLngToTile = (lat, lng, zoom) => {
        const tileX = Math.floor(((lng + 180) / 360) * Math.pow(2, zoom));
        const tileY = Math.floor(
          ((1 -
            Math.log(
              Math.tan((lat * Math.PI) / 180) +
                1 / Math.cos((lat * Math.PI) / 180)
            ) /
              Math.PI) /
            2) *
            Math.pow(2, zoom)
        );
        return { x: tileX, y: tileY };
      };

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const topLeft = latLngToTile(ne.lat(), sw.lng(), zoom);
      const bottomRight = latLngToTile(sw.lat(), ne.lng(), zoom);

      return {
        xRange: [topLeft.x, bottomRight.x],
        yRange: [topLeft.y, bottomRight.y],
      };
    };

    /* const getNormalizedCoord = (coord, zoom) => {
      const y = coord.y;
      let x = coord.x;
      const tileRange = 1 << zoom;
      if (y < 0 || y >= tileRange) {
        return null;
      }
      if (x < 0 || x >= tileRange) {
        x = ((x % tileRange) + tileRange) % tileRange;
      }
      return { x: x, y: y };
    }; */

    const removeHeatmap = (BNUpdate = true) => {
      heatmapRemoved.value = true;
      if (kmlOverlay) {
        kmlOverlay.setMap(null);
        kmlOverlay = null;
        legendUrl.value = null;
        overlayUrl.value = null;
        heatmapFilterData = [];
        emit("manage-base-node", {
          results_exist: checkResultExist.value,
          heatmap_exist: false,
        });
      }
      if (customOverlays) {
        customOverlays.forEach((overlay) => overlay.setMap(null));
        customOverlays = [];
      }
      if (BNUpdate) {
        updateMapWithBaseNodes();
      }
    };

    const removeHeatmapData = () => {
      activeHeatmap.value = null;
      activeHeight.value = null;
      previousHeatmap.value = null;
      previousHeight.value = null;
      activeBaseNode = null;
      activeBNIndex = -1;
    };

    const resetHeatmapfilter = () => {
      activeBands.value = ["3", "5", "6"];
      activeHeatmapRemoved.value = false;
      activeHeightRemoved.value = false;
      heatmapList = [];
      heatmapLegends = [];
      heatmapSuffixes = [];
      sectorsArray = {};
      removeHeatmapData();
      removeHeatmap();
    };

    const setMapType = (type) => {
      if (map) {
        map.setMapTypeId(type);
        mapType.value = type; // Update active map type
      }
    };

    const handleHeatmap = () => {
      enableHeatmap.value = !enableHeatmap.value;
      updateHeatmap();
    };

    const resetAllValues = () => {
      // Clear previous MarkerClusterer and markers
      if (markerClusterer) {
        markerClusterer.clearMarkers();
      }

      if (radiusCircle) {
        radiusCircle.setMap(null);
      }

      if (radiusRectangle) {
        radiusRectangle.setMap(null);
      }

      // Clear existing polyline and markers
      clearMapOverlays();

      if (baseMarker) {
        baseMarker.setMap(null); // Remove base marker
        baseMarker = null; // Reset baseMarker variable
      }

      if (remoteMarker) {
        remoteMarker.setMap(null); // Remove remote marker
        remoteMarker = null; // Reset remoteMarker variable
      }

      if (newRemoteMarker) {
        newRemoteMarker.setMap(null);
        newRemoteMarker = null;
      }

      if (polygonArray) {
        polygonArray.forEach((polygon) => polygon.setMap(null));
        polygonArray = [];
      }

      if (plottedBNMarkers) {
        plottedBNMarkers.forEach((marker) => marker.setMap(null));
        plottedBNMarkers = [];
      }

      if (remoteNodeList) remoteNodeList = [];
      if (selectedRN) selectedRN = null;
      if (selectedRNList) selectedRNList = null;
      if (selectedBN) selectedBN = null;

      sectorResults = [];
      baseNodeList = [];

      newLocation = null;
      lockMapUpdate.value = false;
      resetAll.value = false;
      loadingButton.value = false;
      heatmapLoading.value = false;
      multiSectorFlag.value = false;
      addressChanged.value = false;
      latlngChanged.value = false;
      mapClickEvent.value = false;
      mapDragEvent.value = false;
      clusterClicked.value = false;
      mapFiltering.value = false;
      checkResultExist.value = false;
      modalHeatmapExist.value = false;
      resultsModalRemoved.value = false;
      azimuthPolygonClicked.value = false;
      bandsValueChanged.value = false;
      lockZoom.value = false;
      activeRadius.value = null;

      if (hoverMarkerIcon) {
        hoverMarkerExist.value = false;
        lastHoverLatLng = null;
        hoverMarkerIcon.setVisible(false);
      }

      /* if (clearAllMarkers.value) {
        resetHeatmapfilter();
      } */

      //handleMapLockZoom();

      google.maps.event.clearListeners(map, "click");

      emit("reset-all", {
        results_exist: false,
        heatmap_exist: heatmapImageLoaded.value,
      });

      // Re-add the map click listener
      mapClickListener = map.addListener("click", (event) => {
        mapClickHandler(event.latLng);
      });
    };

    // Define the callback function to initialize the map
    const initMap = () => {
      const options = {
        styles: [
          {
            elementType: "labels",
            stylers: [{ visibility: "on" }],
          },
          {
            featureType: "administrative",
            elementType: "geometry",
            stylers: [{ visibility: "on" }],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ visibility: "on" }],
          },
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [{ visibility: "on" }],
          },
        ],
      };

      let centerLatLng = { lat: 39.8283, lng: -98.5795 };
      let zoomLevel = 5;

      if (localStorage.getItem("remote_lat_lng")) {
        const remoteLatLng = localStorage.getItem("remote_lat_lng");
        const parse = JSON.parse(remoteLatLng);
        centerLatLng = {
          lat: Number(parse.lat),
          lng: Number(parse.lng),
        };
        zoomLevel = 12;
      }

      const mapOptions = {
        center: centerLatLng,
        zoom: zoomLevel,
        tilt: 0, // Ensure the map starts with no tilt
        disableDefaultUI: true,
        mapTypeId: "hybrid",
        scaleControl: true,
      };

      if (mapDivRef.value) {
        //const mapWidth = mapDivRef.value.offsetWidth;
        const mapHeight = window.innerHeight - 40;
        mapDivRef.value.style.height = mapHeight + "px";
      }

      map = new google.maps.Map(mapDivRef.value, mapOptions);

      window.$map = map;

      window.$testRightClickAzimuthPolygon = (serial) => {
        const node = baseNodeList.bn_list.find((n) => n.serial === serial);
        mapRightClickHandler(node);
      };

      window.$testMapClickHandler = (location) => {
        mapClickHandler(location);
      };

      infoWindow = new google.maps.InfoWindow();

      // Create the hover marker once and keep it hidden.
      /* hoverMarkerIcon = new google.maps.Marker({
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "red",
          fillOpacity: 1,
          strokeWeight: 10,
          strokeColor: "red",
          strokeOpacity: 0.4,
        },
        visible: false,
        zIndex: 9999,
      }); */

      const hoverMarkerOverlay = createHoverMarkerOverlay();
      hoverMarkerIcon = new hoverMarkerOverlay(map);

      // Now that Google Maps is loaded, create the LabelOverlay class
      labelOverlay = createLabelOverlay();

      if (!lockMapUpdate.value && !checkResultExist.value) {
        clusterClicked.value = false;
        getMapCenterAndRadius();
        handleZoomDragforBN();
      }

      bnMarkerIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#025287",
        fillOpacity: 1,
        strokeWeight: 10,
        strokeColor: "#025287",
        strokeOpacity: 0.4,
      };

      rnMarkerIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#c14d23",
        fillOpacity: 1,
        strokeWeight: 10,
        strokeColor: "#c14d23",
        strokeOpacity: 0.4,
      };

      // Attach the click event listener to the map
      mapClickListener = map.addListener("click", (event) => {
        mapClickHandler(event.latLng);
      });

      const bnSVGIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
        <path d="M18,0 C25.732,0 32,6.641 32,14.6 C32,27.963 18,40 18,40 C18,40 4,28.064 4,14.6 C4,6.641 10.268,0 18,0 Z" id="Shape" fill="#025287" />
        <circle cx="18" cy="18" r="7" fill="#000"/>
      </svg>`;

      // Encode the SVG to a Data URL
      bnEncodedSVG =
        "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(bnSVGIcon);

      const rnSVGIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
        <path d="M18,0 C25.732,0 32,6.641 32,14.6 C32,27.963 18,40 18,40 C18,40 4,28.064 4,14.6 C4,6.641 10.268,0 18,0 Z" id="Shape" fill="#c14d23" />
        <circle cx="18" cy="18" r="7" fill="#000"/>
      </svg>`;

      // Encode the SVG to a Data URL
      rnEncodedSVG =
        "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(rnSVGIcon);

      window.$testShowBNInfoWindow = (serial) => {
        const index = baseNodeList.bn_list.findIndex(
          (node) => node.serial === serial
        );

        if (index === -1) {
          console.warn(`No base node found with serial: ${serial}`);
          return;
        }

        const node = baseNodeList.bn_list[index];
        const marker = baseNodeMarkers[index];

        if (!marker) {
          console.warn("No marker found for base node!");
          return;
        }
        if (!marker.getMap()) {
          console.warn("Marker has no map! Setting map manually.");
          marker.setMap(map); // Attach it
        }
        showBaseNodeInfoWindow(node, marker);
      };
    };

    onMounted(() => {
      const key = process.env.VUE_APP_GOOGLEMAPS_KEY;

      // Set the initMap function to the window object to ensure it's accessible globally
      window.initMap = initMap;

      // Load the necessary libraries asynchronously
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry,places,visualization&callback=initMap`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      // Event listener for window resize
      window.addEventListener("resize", resizeMap);
    });

    // Watch for changes in props
    watch(
      [
        () => props.operatorKey,
        () => props.loadMapRadius,
        () => props.hoverMarkerData,
        () => props.baseNodeList,
        () => props.remoteAddress,
        () => props.remoteLatLng,
        () => props.sectorResults,
        () => props.activeSector,
        () => props.heatmapData,
        () => props.multiSector,
        () => props.clearRemoteNode,
        () => props.loadingButton,
      ],
      (
        [
          newOperatorKey,
          loadMapRadius,
          newHoverMarkerData,
          newBaseNodeList,
          remoteAddress,
          remoteLatLng,
          newSectorResults,
          newActiveSector,
          newHeatmapData,
          multiSector,
          clearRemoteNode,
          loadButton,
        ],
        [
          prevOperatorKey,
          prevLoadMapRadius,
          prevHoverMarkerData,
          prevBaseNodeList,
          prevRemoteAddress,
          prevRemoteLatLng,
          prevSectorResults,
          prevActiveSector,
          prevHeatmapData,
          prevMultiSector,
          prevClearRemoteNode,
          prevLoadingButton,
        ]
      ) => {
        if (!map) return; // Ensure map exists before proceeding

        loadingButton.value = loadButton;
        multiSectorFlag.value = multiSector;

        if (Object.keys(remoteLatLng).length > 0) {
          newLocation = remoteLatLng;
          latlngChanged.value = true;
        } else {
          latlngChanged.value = false;
        }

        if (loadMapRadius) {
          getMapCenterAndRadius();
        }

        if (newOperatorKey !== prevOperatorKey) {
          baseNodeList = [];
          resetHeatmapfilter();
          clearBaseNodesDisplay();
        }

        if (newHoverMarkerData !== prevHoverMarkerData) {
          hoverMarkerExist.value = true;
          handleHoverMarker(newHoverMarkerData);
        } else {
          lastHoverLatLng = null;
          hoverMarkerExist.value = false;
        }

        if (newActiveSector && newActiveSector !== prevActiveSector) {
          activeSector = newActiveSector;
          handleMapWithSector();
        }

        if (newHeatmapData !== prevHeatmapData && checkResultExist.value) {
          modalHeatmapExist.value = true;
          handleHeatmapFromModal(newHeatmapData);
        } else {
          modalHeatmapExist.value = false;
        }

        if (clearRemoteNode) {
          clearAllMarkers.value = false;
          addressChanged.value = false;
          resetRemoteMarker();
        }

        if (
          newBaseNodeList.bn_list &&
          Object.keys(newBaseNodeList.bn_list).length > 0
        ) {
          checkAzimuthExist.value = true;
          if (newBaseNodeList.suffixes) {
            heatmapSuffixes = newBaseNodeList.suffixes ?? [];
          }
          if (newBaseNodeList.legends) {
            heatmapLegends = newBaseNodeList.legends ?? [];
          }
          baseNodeList = newBaseNodeList;
          if (!hoverMarkerExist.value) {
            updateMapWithBaseNodes();
          }
        } else {
          checkAzimuthExist.value = false;
          baseNodeList = [];
          clearBaseNodesDisplay();
        }

        if (remoteAddress) {
          if (remoteAddress !== prevRemoteAddress) {
            addressChanged.value = true;
            clusterClicked.value = false;
            clearAllMarkers.value = true;
            mapFiltering.value = false;
          }
          geocodeAddress(remoteAddress);
        }

        // Check if sectorResults is an empty object before updating the map
        if (newSectorResults && clearAllMarkers.value) {
          if (newSectorResults !== prevSectorResults) {
            mapFiltering.value = false;
            lockMapUpdate.value = false;
          }
          sectorResults = newSectorResults;
          if (!modalHeatmapExist.value && !lockMapUpdate.value) {
            updateMap();
          }
        }
      },
      { deep: true }
    );

    const handleZoomDragforBN = () => {
      let zooming = false; // Flag to track zooming activity
      let dragging = false; // Flag to track dragging activity
      // Detect when zooming starts
      map.addListener("zoom_changed", () => {
        zooming = true;
      });

      // Detect when dragging starts
      map.addListener("dragstart", () => {
        dragging = true;
      });

      // Detect when dragging ends
      map.addListener("dragend", () => {
        dragging = true; // Mark dragging as started
      });

      // Detect when zooming stops (idle event fires after movement completes)
      map.addListener("idle", () => {
        if (zooming) {
          zooming = false;
          clusterClicked.value = false;
          setTimeout(() => {
            getMapCenterAndRadius();
          }, 1500); // Delay execution by 1.5 seconds after zoom stops
        }

        if (dragging) {
          dragging = false;
          clusterClicked.value = false;
          setTimeout(() => {
            getMapCenterAndRadius();
          }, 1500); // Delay execution by 1.5 second after dragging stops
        }
      });
    };

    // Function to handle map clicks and plot RN marker
    const mapClickHandler = (location) => {
      const clickedLatLng = {
        lat: location.lat(),
        lng: location.lng(),
      };

      mapClickEvent.value = true;
      clearAllMarkers.value = true;
      newLocation = clickedLatLng;
      placeNewRemoteMarker(newLocation);
      updateLocationInfo(clickedLatLng);
    };

    const getMapCenterAndRadius = () => {
      if (!map) return;

      // Get the bounds of the visible map area
      const bounds = map.getBounds();
      const center = map.getCenter();

      if (bounds && center) {
        // Center coordinates
        const centerLat = center.lat();
        const centerLng = center.lng();

        zoomChanged.value = true;

        let radius = calculateRadiusFromBounds();
        activeRadius.value = radius;

        console.log("Center Latitude:", centerLat);
        console.log("Center Longitude:", centerLng);
        console.log("Radius (meters):", radius);

        let zoom = map.getZoom();
        if (zoom < 17) {
          if (
            (!clusterClicked.value && !lockMapUpdate.value) ||
            !checkAzimuthExist.value
          ) {
            // Emit the center and radius info
            emit("update-bn-radius", {
              latitude: centerLat,
              longitude: centerLng,
              radius,
            });
          }
        }
      }
    };

    const calculateRadiusFromBounds = () => {
      // Calculate the rectangle bounds
      const bounds = map.getBounds();

      const boundsRect = {
        north: bounds.getNorthEast().lat(),
        east: bounds.getNorthEast().lng(),
        south: bounds.getSouthWest().lat(),
        west: bounds.getSouthWest().lng(),
      };
      const toRadians = (degrees) => (degrees * Math.PI) / 180;

      const earthRadius = 6371000; // Earth's radius in meters

      // Extract bounds coordinates
      const { north, east, south, west } = boundsRect;

      // Calculate the center of the bounds
      const centerLat = (north + south) / 2;
      const centerLng = (east + west) / 2;

      // Function to calculate distance using Haversine formula
      const haversineDistance = (lat1, lng1, lat2, lng2) => {
        const dLat = toRadians(lat2 - lat1);
        const dLng = toRadians(lng2 - lng1);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return earthRadius * c;
      };

      // Calculate distances to each corner of the bounds
      const distances = [
        haversineDistance(centerLat, centerLng, north, east), // NE corner
        haversineDistance(centerLat, centerLng, north, west), // NW corner
        haversineDistance(centerLat, centerLng, south, east), // SE corner
        haversineDistance(centerLat, centerLng, south, west), // SW corner
      ];

      // Get the maximum distance as the effective radius
      const radius = Math.max(...distances);

      console.log(
        `Radius from center to farthest corner: ${radius.toFixed(2)} meters`
      );
      return radius;
    };

    const geocodeAddress = (address) => {
      if (
        !latlngChanged.value &&
        addressChanged.value &&
        !mapClickEvent.value &&
        !mapDragEvent.value
      ) {
        newLocation = null;
      }
      if (!checkResultExist.value && newRemoteMarker) {
        newRemoteMarker.setMap(null);
        newRemoteMarker = null;
      }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK") {
          const location = results[0].geometry.location;
          let latLng = {
            lat: location.lat(),
            lng: location.lng(),
          };
          if (newLocation) {
            latLng = newLocation;
          }
          if (!newLocation && clearAllMarkers.value) {
            emit("update-lat-lng", {
              coordinates: latLng,
              remote_pin: false,
            });
          }
          placeRemoteNodeMarker(latLng);
        } else {
          console.error(
            "Geocode was not successful for the following reason:",
            status
          );
        }
      });
    };

    const getAddressFromLatLng = (latLng, callback) => {
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === "OK") {
          if (results[0]) {
            const address = results[0].formatted_address;
            callback(address);
          } else {
            console.log("No results found");
            callback(null);
          }
        } else {
          console.error("Geocoder failed due to:", status);
        }
      });
    };

    const resetRemoteMarker = () => {
      if (newRemoteMarker) {
        newRemoteMarker.setMap(null);
        newRemoteMarker = null;
      }
      if (checkResultExist.value && !clearAllMarkers.value) {
        mapClickEvent.value = false;
        clearAllMarkers.value = true;
        emit("update-lat-lng", {
          coordinates: remoteOldLocation,
          remote_pin: false,
        });
      } else {
        resetAllValues();
      }
    };

    const placeNewRemoteMarker = (location) => {
      let rnSvgMarkerIcon = {
        url: rnEncodedSVG, // Use the encoded SVG Data URL
        scaledSize: new google.maps.Size(40, 40), // Set the size of the icon
        anchor: new google.maps.Point(20, 40),
      };

      // Remove existing RN marker, if any
      if (newRemoteMarker) {
        newRemoteMarker.setMap(null);
        newRemoteMarker = null;
      }

      newRemoteMarker = new google.maps.Marker({
        position: location,
        icon: rnSvgMarkerIcon,
        map,
        draggable: false,
        title: "RN Marker",
      });

      newRemoteMarker.addListener("click", (event) => {
        let newLatLng = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        showInfoWindow(newLatLng, newRemoteMarker);
      });

      if (!mapClickEvent.value && addressChanged.value) {
        moveToLocation(location);
      }
    };

    const placeRemoteNodeMarker = (location, bnExist = false) => {
      let rnSvgMarkerIcon = {
        url: rnEncodedSVG, // Use the encoded SVG Data URL
        scaledSize: new google.maps.Size(40, 40), // Set the size of the icon
        anchor: new google.maps.Point(20, 40),
      };

      let remoteMarkerIcon = rnSvgMarkerIcon;
      if (bnExist) {
        remoteMarkerIcon = rnMarkerIcon;
      }
      remoteLocation = location;

      if (
        addressChanged.value &&
        clearAllMarkers.value &&
        checkResultExist.value &&
        remoteLocation != remoteOldLocation &&
        !azimuthPolygonClicked.value
      ) {
        if (newLocation) {
          remoteLocation = newLocation;
        }
        emit("update-lat-lng", {
          coordinates: remoteLocation,
          remote_pin: true,
        });
        placeNewRemoteMarker(remoteLocation);
      }

      if (!remoteMarker && clearAllMarkers.value) {
        remoteMarker = new google.maps.Marker({
          position: location,
          icon: remoteMarkerIcon,
          map,
          draggable: false,
          title: "RN Marker",
        });

        /* remoteMarker.addListener("dragend", (event) => {
          const newLatLng = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };

          newLocation = newLatLng;
          mapDragEvent.value = true;
          updateLocationInfo(newLatLng);
          moveToLocation(newLocation);
        }); */

        remoteMarker.addListener("click", (event, newLocation) => {
          let newLatLng = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };
          if (newLocation) newLatLng = newLocation;
          showInfoWindow(newLatLng, remoteMarker);
        });

        remoteOldLocation = location;

        if (!mapClickEvent.value && addressChanged.value) {
          moveToLocation(location);
        }

        if (
          !checkResultExist.value &&
          addressChanged.value &&
          !mapClickEvent.value &&
          !mapDragEvent.value
        ) {
          getMapCenterAndRadius();
        }
      }
      azimuthPolygonClicked.value = false;
      mapClickEvent.value = false;
      mapDragEvent.value = false;
      latlngChanged.value = false;
      addressChanged.value = false;
    };

    const moveToLocation = (location) => {
      const currentCenter = map.getCenter();

      // Check if the new position is different from the current center
      if (
        currentCenter.lat() !== location.lat ||
        currentCenter.lng() !== location.lng
      ) {
        // If different, pan the map to the new position
        map.panTo(new google.maps.LatLng(location.lat, location.lng));
      }
    };

    const updateLocationInfo = (latLng) => {
      getAddressFromLatLng(latLng, (address) => {
        emit("update-lat-lng", {
          coordinates: latLng,
          address,
          clicked: mapClickEvent.value,
          remote_pin: newRemoteMarker && checkResultExist.value ? true : false,
        });
      });
    };

    const showBaseNodeInfoWindow = (node, baseNodeMarker) => {
      const contentString = `
        <div class="info-window">
          <h3>BN Information</h3>
          <table class="table">
            <tr>
              <td class="fw-bold">Operator Name</td>
              <td>${node.operator}</td>
            </tr>
            <tr>
              <td class="fw-bold">Region Name</td>
              <td>${node.region}</td>
            </tr>
            <tr>
              <td class="fw-bold">Market Name</td>
              <td>${node.market}</td>
            </tr>
            <tr>
              <td class="fw-bold">Site Name</td>
              <td>${node.site}</td>
            </tr>
            <tr>
              <td class="fw-bold">Cell Name</td>
              <td>${node.cell}</td>
            </tr>
            <tr>
              <td class="fw-bold">Host Name</td>
              <td>${node.hostname}</td>
            </tr>
            <tr>
              <td class="fw-bold">Sector Name</td>
              <td>${node.sector}</td>
            </tr>
            <tr>
              <td class="fw-bold">Serial Number</td>
              <td>${node.serial}</td>
            </tr>
            <tr>
              <td class="fw-bold">Latest Timestamp</td>
              <td>${formatDate(node.timestamp)}</td>
            </tr>
            <tr>
              <td class="fw-bold">Firmware Version</td>
              <td>${node.firmware}</td>
            </tr>
            <tr>
              <td class="fw-bold">Subscriber Count</td>
              <td>${node.subscribers}</td>
            </tr>
            <tr>
              <td class="fw-bold">Latitude</td>
              <td>${node.latitude.toFixed(6)}</td>
            </tr>
            <tr>
              <td class="fw-bold">Longitude</td>
              <td>${node.longitude.toFixed(6)}</td>
            </tr>
            <tr>
              <td class="fw-bold">Height (AGL)</td>
              <td>${
                node.height_agl ? `${node.height_agl.toFixed(2)} meters` : ""
              }</td>
            </tr>
            <tr>
              <td class="fw-bold">Height (MSL)</td>
              <td>${
                node.height_gps ? `${node.height_gps.toFixed(2)} meters` : ""
              }</td>
            </tr>
            <tr>
              <td class="fw-bold">Azimuth</td>
              <td>${
                node.azimuth
                  ? `${node.azimuth.toFixed(2)} degrees (cw from north)`
                  : ""
              }</td>
            </tr>
            <tr>
              <td class="fw-bold">Downtilt</td>
              <td>${
                node.downtilt ? `${node.downtilt.toFixed(2)} degrees` : ""
              }</td>
            </tr>
            <tr>
              <td class="fw-bold">Carrier Frequencies</td>
              <td>${formatValues("frequency", node.carrier)}</td>
            </tr>
            <tr>
              <td class="fw-bold">Carrier Bandwidths</td>
              <td>${formatValues("bandwidth", node.carrier)}</td>
            </tr>
            <tr>
              <td class="fw-bold">Carrier TX Powers</td>
              <td>${formatValues("tx_power", node.carrier)}</td>
            </tr>
          </table>
        </div>
      `;

      if (infoWindow) infoWindow.close();
      infoWindow.setContent(contentString);
      infoWindow.open(map, baseNodeMarker);

      emit("update-base-node", {
        base_node: node,
        from_result: false,
      });
    };

    const showInfoWindow = (latLng, marker) => {
      getAddressFromLatLng(latLng, (address) => {
        let newLatitude = latLng.lat.toFixed(6);
        let newLongitude = latLng.lng.toFixed(6);
        const contentString = `
        <div class="info-window">
          <h3>Location Info</h3>
          <table class="table">
            <tr>
              <td class="fw-bold">Address</td>
              <td>${address || "N/A"}</td>
            </tr>
            <tr>
              <td class="fw-bold">Latitude</td>
              <td>${newLatitude}</td>
            </tr>
            <tr>
              <td class="fw-bold">Longitude</td>
              <td>${newLongitude}</td>
            </tr>
          </table>
        </div>
      `;

        if (infoWindow) infoWindow.close();
        infoWindow.setContent(contentString);
        infoWindow.open(map, marker);
      });
    };

    const calculateCenterPoint = (nodes) => {
      const totalNodes = nodes.length;
      const sumLat = nodes.reduce((sum, node) => sum + node.latitude, 0);
      const sumLng = nodes.reduce((sum, node) => sum + node.longitude, 0);

      return {
        lat: sumLat / totalNodes,
        lng: sumLng / totalNodes,
      };
    };

    const clearBaseNodesDisplay = () => {
      // Clear previous MarkerClusterer and markers
      if (markerClusterer) {
        markerClusterer.clearMarkers();
      }

      baseNodeMarkers.forEach((marker) => marker.setMap(null));
      baseNodeMarkers = [];

      polygonArray.forEach((polygon) => polygon.setMap(null));
      polygonArray = [];

      if (radiusCircle) {
        radiusCircle.setMap(null);
      }

      if (radiusRectangle) {
        radiusRectangle.setMap(null);
      }
    };

    const updateMapWithBaseNodes = () => {
      clusterClicked.value = false;

      // Define MarkerClusterer options
      const clusterOptions = {
        minimumClusterSize: 2,
        maxZoom: 15,
        renderer: {
          render: ({ count, position }) => {
            return new google.maps.Marker({
              position,
              label: {
                text: String(count),
                color: "white",
                fontSize: "10px",
              },
              title: `Cluster of ${count} markers`,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: count > 10 ? "#E11A16" : "#101CDD",
                fillOpacity: 0.8,
                strokeWeight: 8,
                strokeColor: count > 10 ? "#E11A16" : "#101CDD",
                strokeOpacity: 0.4,
              },
            });
          },
        },
      };

      clearBaseNodesDisplay();

      if (!baseNodeList.showBNRadius) {
        return;
      }

      if (baseNodeList.bn_list.length > 0) {
        let baseNodeRadius = parseInt(baseNodeList.radius);
        let centerPoint = calculateCenterPoint(baseNodeList.bn_list);

        if (!baseNodeList.rectangleAPICall) {
          radiusCircle = new google.maps.Circle({
            strokeColor: "#4285F4",
            strokeOpacity: 0.6,
            strokeWeight: 2,
            fillColor: "#4285F4",
            fillOpacity: 0.1,
            map,
            center: centerPoint,
            radius: baseNodeRadius,
          });
        }

        baseNodeList.bn_list.forEach((node, index) => {
          let lat = node.latitude;
          let lng = node.longitude;

          // Extract valid frequencies and convert them to GHz
          let frequenciesGHz = node.carrier
            .map((c) => Math.floor(c.frequency / 1000))
            .filter((f) => f > 0)
            .map((f) => f.toString());

          // Check if at least one frequency is active
          let hasActiveFrequency = frequenciesGHz.some((f) =>
            activeBands.value.includes(f)
          );

          if (!hasActiveFrequency) {
            if (
              activeBaseNode &&
              activeBaseNode.serial == node.serial &&
              activeHeatmap.value &&
              activeHeight.value
            ) {
              removeHeatmapData();
              removeHeatmap(false);
            }
            return; // Hide this BN (skip further processing)
          }

          heatmapArray[node.serial] = node.heatmap_list || [];

          if (node.azimuth !== null && node.azimuth !== undefined) {
            const azimuthAngle = (node.azimuth * Math.PI) / 180; // Convert azimuth to radians
            const spreadAngle = 40 * (Math.PI / 180); // ±40° spread angle (converted to radians)
            const distance = 0.01; // Distance for the polygon edges (adjust based on zoom level)
            const steps = 50; // Number of steps to create a smooth curve
            const arcPoints = [];

            // Generate arc points along the curve (top of the arc)
            for (let i = 0; i <= steps; i++) {
              const angle =
                azimuthAngle - spreadAngle + (i * 2 * spreadAngle) / steps; // Calculate each angle in the arc

              // Calculate latitude and longitude offsets using spherical geometry
              const latOffset = distance * Math.cos(angle); // Latitude offset based on the angle
              const lngOffset = distance * Math.sin(angle); // Longitude offset based on the angle

              // Correct longitude for Earth's curvature by dividing by the cosine of latitude
              arcPoints.push({
                lat: lat + latOffset, // Latitude change
                lng: lng + lngOffset / Math.cos((lat * Math.PI) / 180), // Longitude adjustment for curvature
              });
            }

            // Construct the polygon path for the sector, including the arc and arrow
            const polygonPath = [
              { lat, lng }, // Start point (center of the sector)
              ...arcPoints, // Arc points along the curve
              { lat, lng }, // Close the polygon by going back to the center (this ensures closure)
            ];

            const isActive =
              activeBaseNode &&
              activeBaseNode.serial === node.serial &&
              !heatmapRemoved.value;

            // Draw the sector as a filled polygon
            const azimuthPolygon = new google.maps.Polygon({
              paths: polygonPath, // Use the constructed path
              strokeColor: isActive ? "#FF5733" : "#438973", // Outline color
              strokeOpacity: 1, // Outline opacity
              strokeWeight: 2, // Outline thickness
              fillColor: isActive ? "#FF5733" : "#5ebc8b", // Fill color
              fillOpacity: 0.4, // Fill opacity
              map, // Add to the map
              clickable: true, // Enable clicks
              zIndex: 1000 + index,
            });

            // Create tooltip
            const tooltip = document.createElement("div");
            tooltip.innerHTML = "Right Click for Heatmap";
            tooltip.style.position = "absolute";
            tooltip.style.background = "#000";
            tooltip.style.color = "#fff";
            tooltip.style.padding = "5px";
            tooltip.style.fontSize = "12px";
            tooltip.style.borderRadius = "4px";
            tooltip.style.display = "none";
            tooltip.style.pointerEvents = "none";
            document.body.appendChild(tooltip);

            // **Show Tooltip on Hover**
            google.maps.event.addListener(
              azimuthPolygon,
              "mouseover",
              (event) => {
                tooltip.style.display = "block";
                tooltip.style.left = event.domEvent.clientX + 10 + "px";
                tooltip.style.top = event.domEvent.clientY + 10 + "px";
              }
            );

            google.maps.event.addListener(
              azimuthPolygon,
              "mousemove",
              (event) => {
                tooltip.style.left = event.domEvent.clientX + 10 + "px";
                tooltip.style.top = event.domEvent.clientY + 10 + "px";
              }
            );

            google.maps.event.addListener(azimuthPolygon, "mouseout", () => {
              tooltip.style.display = "none";
            });

            // Right Click - Bind Heatmap to Base Node
            google.maps.event.addListener(azimuthPolygon, "rightclick", () => {
              document.addEventListener(
                "contextmenu",
                (e) => e.preventDefault(),
                { once: true }
              );

              mapRightClickHandler(node);
            });

            // **Left Click - Plot RN Marker**
            mapClickListener = google.maps.event.addListener(
              azimuthPolygon,
              "click",
              (event) => {
                mapClickHandler(event.latLng);
              }
            );

            // Add the polygon to the array for later removal
            polygonArray.push(azimuthPolygon);
            //polylineArray.push(azimuthPolyline);
          }

          const baseNodeMarker = new google.maps.Marker({
            position: { lat, lng },
            icon: bnMarkerIcon,
            title: "BN Marker",
          });

          baseNodeMarker.addListener("click", () => {
            showBaseNodeInfoWindow(node, baseNodeMarker);
          });

          baseNodeMarkers.push(baseNodeMarker);
        });

        // Initialize MarkerClusterer with the markers
        markerClusterer = new MarkerClusterer({
          markers: baseNodeMarkers,
          map: map,
          ...clusterOptions,
        });

        // Listen for cluster click
        markerClusterer.addListener("click", (event) => {
          clusterClicked.value = true;
        });
      }
    };

    const mapRightClickHandler = (node) => {
      emit("update-base-node", {
        base_node: node,
        from_result: false,
      });
      azimuthPolygonClicked.value = true;
      if (!heatmapLoading.value) {
        if (
          activeBaseNode == node &&
          activeHeatmap.value &&
          activeHeight.value &&
          enableHeatmap.value
        ) {
          // If the same node is clicked again, toggle the heatmap off
          activeBaseNode = null; // Reset the activeBaseNode
          removeHeatmap();
        } else {
          // Restore previous values if one of them is missing
          if (!activeHeatmap.value && activeHeight.value) {
            activeHeatmap.value = previousHeatmap.value || "confidence";
          }
          if (!activeHeight.value && activeHeatmap.value) {
            activeHeight.value = previousHeight.value || "3_10";
          }
          if (!activeHeight.value && !activeHeatmap.value) {
            activeHeatmap.value = "confidence";
            activeHeight.value = "3_10";
            previousHeatmap.value = "confidence";
            previousHeight.value = "3_10";
          }

          // Now set the activeBaseNode to the newly clicked node
          activeBaseNode = node;
          heatmapList = node.heatmap_list ?? [];
          heatmapImageLoaded.value = false;
          activeHeightRemoved.value = false;
          updateHeatmap();
          if (checkResultExist.value) {
            mapFiltering.value = true;
            updateMap();
          }
        }
      }
    };

    const formatDate = (timestamp) => {
      if (timestamp) {
        let date = new Date(timestamp);
        const formattedDate =
          date.getFullYear() +
          "-" +
          String(date.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(date.getDate()).padStart(2, "0");
        return formattedDate;
      }
      return "";
    };

    const getMidpoint = (baseNode, remoteNode, isSameDirection = false) => {
      let latMid = (baseNode.lat() + remoteNode.lat()) / 2;
      let lngMid = (baseNode.lng() + remoteNode.lng()) / 2;
      if (isSameDirection) {
        // Toggle usedNodeforMidpoint between baseNode and remoteNode
        if (!usedNodeforMidpoint || usedNodeforMidpoint === remoteNode) {
          usedNodeforMidpoint = baseNode;
        } else {
          usedNodeforMidpoint = remoteNode;
        }

        setLatMidpoint = latMid;
        setLngMidpoint = lngMid;
        latMid = (usedNodeforMidpoint.lat() + setLatMidpoint) / 2;
        lngMid = (usedNodeforMidpoint.lng() + setLngMidpoint) / 2;
      }
      return new google.maps.LatLng(latMid, lngMid);
    };

    // Helper function to extract and format values
    const formatValues = (key, data) => {
      if (data.length > 0) {
        let carrier = data
          .filter((item) => item[key] !== 0) // Exclude entries with zero values
          .map((item) => item[key])
          .join(", ");
        carrier += carrier ? (key == "tx_power" ? " dB" : " MHz") : "";
        return carrier;
      }
      return "";
    };

    const drawSingleSectorPolyline = (baseNode, remoteNode) => {
      const baseLatLng = new google.maps.LatLng(baseNode.lat, baseNode.lng);
      const remoteLatLng = new google.maps.LatLng(
        remoteNode.lat,
        remoteNode.lng
      );
      // Define bounds to encompass both base and remote nodes
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(baseLatLng);
      bounds.extend(remoteLatLng);

      /* if (!mapFiltering.value && !mapClickEvent.value) {
        // Add extra padding to the bounds (e.g., upward)
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        const verticalPadding = 0.1; // Adjust this value for more/less padding
        const adjustedBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(
            southWest.lat() - verticalPadding,
            southWest.lng()
          ),
          new google.maps.LatLng(
            northEast.lat() + verticalPadding,
            northEast.lng()
          )
        );

        // Fit the map to these adjusted bounds
        map.fitBounds(adjustedBounds);
      } */

      // Calculate distance between base and remote nodes
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        baseLatLng,
        remoteLatLng
      );

      const distanceText = `${(distance / 1000).toFixed(2)} km`;

      /* if (!mapFiltering.value && !mapClickEvent.value) {
        // Adjust zoom level based on distance
        let zoomLevel = 12; // Default zoom level
        if (distance < 1000) {
          zoomLevel = 14; // Adjust zoom level for closer distances
        } else if (distance > 10000) {
          zoomLevel = 12; // Adjust zoom level for closer distances
        } else if (distance > 100000) {
          zoomLevel = 10; // Adjust zoom level for closer distances
        } else if (distance > 1000000) {
          zoomLevel = 8; // Adjust zoom level for larger distances
        } else if (distance > 10000000) {
          zoomLevel = 6;
        }

        // Set the map's zoom to the calculated max zoom level if it exceeds it
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
          if (map.getZoom() > zoomLevel) {
            map.setZoom(zoomLevel);
          }
        });
      } */

      /* const height = selectedRN?.mount?.height_agl ?? 0;
      let heightType = selectedRN?.mount?.location ?? "";
      let heightText = "AGL: " + height + "m";
      if (heightType) {
        heightType = heightType.replace(/\b\w/g, (char) => char.toUpperCase());
        heightText += " (" + heightType + ")";
      } */

      let showResultsModal = false;

      if (!activeBaseNode || activeBaseNode?.serial === selectedBN.serial) {
        labelBackground = "#0084b4";
        resultsModalRemoved.value = false;
        showResultsModal = !mapClickEvent.value ? true : false;
      } else {
        labelBackground = "#15202b";
        resultsModalRemoved.value = true;
      }

      const LCC = selectedRN?.aiml_output?.link_close_confidence ?? 0;
      const PL = getPathLoss(selectedRN);

      const midpoint = getMidpoint(baseLatLng, remoteLatLng);
      const mapLabel =
        distanceText +
        " | LCC: " +
        (LCC * 100).toFixed(0) +
        "%" +
        " | PL: " +
        PL;

      // Determine line color based on LCC
      let strokeColor;

      if (LCC > 0.75) {
        strokeColor = "#008000"; // Green for 0.76 and above
      } else if (LCC > 0.5) {
        strokeColor = "#FFFF00"; // Yellow for 0.51 - 0.75
      } else if (LCC > 0.25) {
        strokeColor = "#FF0000"; // Red for 0.26 - 0.50
      } else {
        return;
      }

      /* const lineSymbol = {
        path: google.maps.SymbolPath.CIRCLE,
        fillOpacity: 1,
        scale: 0.8,
      };

      // Draw a polyline between the two points
      route = new google.maps.Polyline({
        path: [baseLatLng, remoteLatLng],
        geodesic: true,
        strokeColor: "#000000",
        strokeOpacity: 0.2,
        strokeWeight: 2,
        icons: [
          {
            icon: lineSymbol,
            offset: "0",
            repeat: "3px",
          },
        ],
      }); */

      // Draw a polyline between the two points
      route = new google.maps.Polyline({
        path: [baseLatLng, remoteLatLng],
        geodesic: true,
        strokeColor: strokeColor,
        strokeOpacity: 1.0,
        strokeWeight: 5,
        map,
        clickable: true,
        zIndex: 2002,
      });

      route.addListener("click", () => {
        mapClickEvent.value = false;
        removePolylineHoverHandlers(route);
        addPolylineHoverHandlers(route, baseNode, remoteNode);
        emit("update-results-modal", {
          remote_data: selectedRN,
          remote_list: selectedRNList,
          heatmap: heatmapArray[selectedBN.serial],
          line_click: true,
        });
        emit("update-base-node", {
          base_node: selectedBN,
          from_result: true,
        });
      });

      labelMarker = new labelOverlay(
        midpoint,
        mapLabel,
        map,
        () => {
          mapClickEvent.value = false;
          removePolylineHoverHandlers(route);
          addPolylineHoverHandlers(route, baseNode, remoteNode);
          emit("update-results-modal", {
            remote_data: selectedRN,
            remote_list: selectedRNList,
            heatmap: heatmapArray[selectedBN.serial],
            line_click: true,
          });
          emit("update-base-node", {
            base_node: selectedBN,
            from_result: true,
          });
        },
        {
          backgroundColor: "#0084b4",
        }
      );

      // Highlight selected base node
      labelMarker.setStyle({
        backgroundColor: labelBackground,
      });

      // Draw the border (bottom layer)
      borderLine = new google.maps.Polyline({
        path: [baseLatLng, remoteLatLng],
        geodesic: true,
        strokeColor: labelBackground,
        strokeOpacity: 1.0,
        strokeWeight: 10,
        map,
        clickable: false,
        zIndex: 2001,
      });

      if (resultsModalRemoved.value) {
        emit("update-results-modal", {
          remove_result: true,
        });
      } else {
        removePolylineHoverHandlers(route);
        addPolylineHoverHandlers(route, baseNode, remoteNode);
        // Set polyline on the map
        emit("update-results-modal", {
          remote_data: selectedRN,
          remote_list: selectedRNList,
          heatmap: heatmapArray[selectedBN.serial],
          line_click: showResultsModal,
          remove_result: false,
          height: activeHeight.value,
        });
        emit("update-base-node", {
          base_node: selectedBN,
          from_result: true,
          sector_options: {},
        });
      }
      repositionMapPoints();
    };

    const selectBestHeight = (remoteNodes) => {
      let heightOptions = [];
      remoteNodes.forEach((remote, index) => {
        //const height = remote?.rn?.heights?.above_open ?? 0;
        const height = remote?.mount?.height_agl ?? 0;
        const LCC = remote?.aiml_output?.link_close_confidence ?? 0;

        if (LCC >= 0.25) {
          heightOptions.push({ index, height, LCC, remote });
          // Sort by LCC (descending) and height (ascending)
          heightOptions.sort((a, b) => {
            if (b.LCC !== a.LCC) return b.LCC - a.LCC; // Higher LCC first
            return a.height - b.height; // Lower height first if LCC is the same
          });
        }
      });

      // Return the best height option
      return heightOptions.length > 0 ? heightOptions[0] : null;
    };

    const getPathLoss = (remoteInfo) => {
      if (remoteInfo?.aiml_output?.path_loss?.average !== undefined) {
        return (
          Math.round(parseFloat(remoteInfo?.aiml_output?.path_loss?.average)) +
          " dB"
        );
      }
      return "";
    };

    const clearMapOverlays = () => {
      // Clear previous polylines and labels
      if (route) {
        route.setMap(null);
        route = null;
      }

      if (borderLine) {
        borderLine.setMap(null);
        borderLine = null;
      }

      if (labelMarker) {
        labelMarker.setMap(null); // Remove label marker
        labelMarker = null; // Reset label marker
      }

      if (polylineArray.length > 0) {
        polylineArray.forEach((polyline) => polyline.setMap(null));
        polylineArray = [];
      }

      if (labelMarkersArray.length > 0) {
        labelMarkersArray.forEach((label) => label.setMap(null));
        labelMarkersArray = [];
      }
    };

    const drawMultiSectorPolyline = (baseNodes, remoteNodes) => {
      if (remoteNodes.length === 0 || baseNodes.length === 0) return;

      clearMapOverlays();

      // Define remoteNode
      const remoteNode = {
        lat: parseFloat(remoteNodes[0].rn.latitude),
        lng: parseFloat(remoteNodes[0].rn.longitude),
      };

      const remoteLatLng = new google.maps.LatLng(
        remoteNode.lat,
        remoteNode.lng
      );

      // Prioritize activeBaseNode
      if (activeBaseNode) {
        activeBNIndex = baseNodes.findIndex(
          (bn) => bn.serial === activeBaseNode.serial
        );
      }

      // Define bounds to encompass both base and remote nodes
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(remoteLatLng);

      /* if (!mapFiltering.value && !mapClickEvent.value) {
        // Add extra padding to the bounds (e.g., upward)
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        const verticalPadding = 0.1; // Adjust this value for more/less padding
        const adjustedBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(
            southWest.lat() - verticalPadding,
            southWest.lng()
          ),
          new google.maps.LatLng(
            northEast.lat() + verticalPadding,
            northEast.lng()
          )
        );

        // Fit the map to these adjusted bounds
        map.fitBounds(adjustedBounds);
      } */

      const bestHeightData = selectBestHeight(remoteNodes);

      //let highestLCC = 0;
      let selectedBNDistance = Infinity;
      let selectedRemoteNodeList = null;
      let selectedRemoteNode = null;
      let selectedBaseNode = null;
      let storedPairs = [];
      let showResultsModal = false;
      let sectorTempList = [];
      sectorsArray = {};

      if (activeBaseNode && !bandsValueChanged.value) {
        labelBackground = "#15202b";
        resultsModalRemoved.value = true;
      } else {
        labelBackground = "#0084b4";
        resultsModalRemoved.value = false;
      }

      baseNodes.forEach((bn, index) => {
        if (bn.latitude && bn.longitude) {
          const baseNode = {
            lat: parseFloat(bn.latitude),
            lng: parseFloat(bn.longitude),
          };

          const baseLatLng = new google.maps.LatLng(baseNode.lat, baseNode.lng);

          // Calculate distance
          const distance =
            google.maps.geometry.spherical.computeDistanceBetween(
              baseLatLng,
              remoteLatLng
            );

          const distanceText = `${(distance / 1000).toFixed(2)} km`;

          /* if (!mapFiltering.value && !mapClickEvent.value) {
            // Adjust zoom level based on distance
            let zoomLevel = 12;
            if (distance < 1000) zoomLevel = 14;
            else if (distance > 10000) zoomLevel = 12;
            else if (distance > 100000) zoomLevel = 10;
            else if (distance > 1000000) zoomLevel = 8;
            else if (distance > 10000000) zoomLevel = 6;

            // Set the map's zoom level
            google.maps.event.addListenerOnce(map, "bounds_changed", () => {
              if (map.getZoom() > zoomLevel) {
                map.setZoom(zoomLevel);
              }
            });
          } */

          /* const height = remoteNodes[index]?.mount?.height_agl ?? 0;
          let heightType = remoteNodes[index]?.mount?.location ?? "";
          let heightText = "AGL: " + height + "m";
          if (heightType) {
            heightType = heightType.replace(/\b\w/g, (char) =>
              char.toUpperCase()
            );
            heightText += " (" + heightType + ")";
          } */

          const LCC =
            remoteNodes[index]?.aiml_output?.link_close_confidence ?? 0;
          const PL = getPathLoss(remoteNodes[index]);
          const sectorValue =
            bn.sector +
            " - " +
            bn.azimuth +
            "\u00B0 - LCC: " +
            (LCC * 100).toFixed(0) +
            "%";

          // Store for sorting later
          sectorTempList.push({
            sector: bn.sector,
            label: sectorValue,
            lcc: LCC,
          });

          // Determine line color based on LCC
          let strokeColor;

          if (LCC > 0.75) {
            strokeColor = "#008000"; // Green for 0.76 and above
          } else if (LCC > 0.5) {
            strokeColor = "#FFFF00"; // Yellow for 0.51 - 0.75
          } else if (LCC > 0.25) {
            strokeColor = "#FF0000"; // Red for 0.26 - 0.50
          } else {
            return; // Skip plotting for LCC ≤ 0.25
          }

          // Determine if this BN should be selected for the modal
          if (bestHeightData) {
            const useIndex =
              activeBNIndex !== -1
                ? activeBNIndex
                : bestHeightData?.index ?? index;

            if (useIndex === index) {
              selectedBaseNode =
                activeBaseNode?.serial === bn.serial ? activeBaseNode : bn;
              selectedRemoteNode = remoteNodes[useIndex];
              selectedRemoteNodeList = remoteNodeList[useIndex];
              selectedBNDistance = distance;
            }
          }

          if (activeBaseNode?.serial === bn.serial) {
            labelBackground = "#0084b4";
            resultsModalRemoved.value = false;
            showResultsModal = !mapClickEvent.value ? true : false;
          }

          // Draw a polyline with visible dashed symbols
          const polyline = new google.maps.Polyline({
            path: [baseLatLng, remoteLatLng],
            geodesic: true,
            strokeColor: strokeColor,
            strokeOpacity: 1.0,
            strokeWeight: 5,
            map,
            clickable: true,
            zIndex: selectedBaseNode?.serial === bn.serial ? 2002 : 2000,
          });

          // Compute angle between BN and RN
          const deltaLat = remoteNode.lat - baseNode.lat;
          const deltaLng = remoteNode.lng - baseNode.lng;
          const angle = Math.atan2(deltaLat, deltaLng) * (180 / Math.PI);

          let isSameDirection = false;

          // Check stored pairs for nearly same direction (±5° tolerance)
          for (let pair of storedPairs) {
            const existingAngle = pair.angle;
            if (Math.abs(existingAngle - angle) < 5) {
              isSameDirection = true;
              break;
            }
          }

          // Store only if it's unique in direction
          storedPairs.push({
            bn,
            remoteNode,
            angle,
          });

          const midpoint = getMidpoint(
            baseLatLng,
            remoteLatLng,
            isSameDirection
          );
          const mapLabel =
            distanceText +
            " | LCC: " +
            (LCC * 100).toFixed(0) +
            "%" +
            " | PL: " +
            PL;

          const mapLabelMarker = new labelOverlay(
            midpoint,
            mapLabel,
            map,
            () => {
              mapClickEvent.value = false;
              selectedRN = remoteNodes[index];
              selectedBN = bn;
              activeBaseNode = selectedBN;
              selectedRNList = remoteNodeList[index];
              polylineArray.forEach((line) => {
                removePolylineHoverHandlers(line);
              });

              addPolylineHoverHandlers(polyline, baseNode, remoteNode);
              emit("update-results-modal", {
                remote_data: selectedRN,
                remote_list: selectedRNList,
                heatmap: heatmapArray[selectedBN.serial],
                line_click: true,
              });
              emit("update-base-node", {
                base_node: selectedBN,
                from_result: true,
              });

              drawMultiSectorPolyline(baseNodes, remoteNodes);
            },
            {
              backgroundColor: "#0084b4",
            }
          );

          polyline.addListener("click", () => {
            selectedBN = bn;
            selectedRNList = remoteNodeList[index];
            const bestHeight = selectBestHeight(remoteNodeList[index]);
            selectedRN = remoteNodes[bestHeight.index];
            if (
              mapLabelMarker &&
              typeof mapLabelMarker.handleLabelClick === "function"
            ) {
              mapLabelMarker.handleLabelClick();
            }

            if (
              mapLabelMarker &&
              typeof mapLabelMarker.onClick === "function"
            ) {
              mapLabelMarker.onClick();
            }
          });

          // Highlight selected base node
          if (selectedBaseNode?.serial === bn.serial) {
            mapLabelMarker.setStyle({
              backgroundColor: labelBackground,
            });

            // Draw the border (bottom layer)
            const borderPolyline = new google.maps.Polyline({
              path: [baseLatLng, remoteLatLng],
              geodesic: true,
              strokeColor: labelBackground,
              strokeOpacity: 1.0,
              strokeWeight: 10,
              map,
              clickable: false,
              zIndex: 2001,
            });

            polylineArray.push(borderPolyline);
            removePolylineHoverHandlers(polyline);
            addPolylineHoverHandlers(polyline, baseNode, remoteNode);
          }

          // Store the polyline in the array and add to the map
          polylineArray.push(polyline);
          labelMarkersArray.push(mapLabelMarker);
        }
      });

      sectorTempList.sort((a, b) => b.lcc - a.lcc);
      sectorTempList.forEach((item) => {
        sectorsArray[item.sector] = item.label;
      });

      if (resultsModalRemoved.value) {
        emit("update-results-modal", { remove_result: true });
      } else if (selectedRemoteNode && selectedBaseNode) {
        selectedRN = selectedRemoteNode;
        selectedRNList = selectedRemoteNodeList;
        emit("update-results-modal", {
          remote_data: selectedRemoteNode,
          remote_list: selectedRemoteNodeList,
          heatmap: heatmapArray?.[selectedBaseNode.serial],
          line_click: showResultsModal ? true : false,
          remove_result: false,
          height: mapClickEvent.value ? activeHeight.value : null,
        });
        emit("update-base-node", {
          base_node: selectedBaseNode,
          from_result: true,
          sector_options: sectorsArray,
        });
      } else {
        emit("update-results-modal");
      }
      repositionMapPoints();
    };

    const addPolylineHoverHandlers = (polyline, baseNode, remoteNode) => {
      const mousemove = google.maps.event.addListener(
        polyline,
        "mousemove",
        (event) => {
          const cursorLng = event.latLng.lng();
          const cursorLat = event.latLng.lat();
          const Ax = baseNode.lng,
            Ay = baseNode.lat;
          const Bx = remoteNode.lng,
            By = remoteNode.lat;

          const ABx = Bx - Ax,
            ABy = By - Ay;
          const APx = cursorLng - Ax,
            APy = cursorLat - Ay;

          const ab_squared = ABx * ABx + ABy * ABy;
          const ap_dot_ab = APx * ABx + APy * ABy;

          let percentage = ap_dot_ab / ab_squared;
          percentage = Math.max(0, Math.min(1, percentage));

          /* const markerLat = Ay + percentage * (By - Ay);
          const markerLng = Ax + percentage * (Bx - Ax);

          handleHoverMarker({ lat: markerLat, lng: markerLng }); */

          emit("map-path-hover", { percentage });
        }
      );

      const mouseout = google.maps.event.addListener(
        polyline,
        "mouseout",
        () => {
          /* if (typeof handleHoverMarker === "function") {
            handleHoverMarker();
          } */
          emit("map-path-hover");
        }
      );

      // Store for later removal
      polyline._hoverHandlers = [mousemove, mouseout];
    };

    const removePolylineHoverHandlers = (polyline) => {
      if (polyline._hoverHandlers) {
        polyline._hoverHandlers.forEach((handler) =>
          google.maps.event.removeListener(handler)
        );
        polyline._hoverHandlers = null;
      }
    };

    const repositionMapPoints = () => {
      /* if (!mapFiltering.value && !mapClickEvent.value) {
        // Reposition the map center slightly upward to move the polyline toward the top
        google.maps.event.addListenerOnce(map, "idle", () => {
          const center = map.getCenter();
          const mapHeightOffset = 0.1; // Adjust this value to control how much the map shifts
          const adjustedLatLng = {
            lat: center.lat() - mapHeightOffset,
            lng: center.lng(),
          };
          moveToLocation(adjustedLatLng);
        });
      } */

      if (!checkAzimuthExist.value) {
        getMapCenterAndRadius();
      }

      clearZoomAndMapClick();
    };

    const clearZoomAndMapClick = () => {
      resetAll.value = true;
      clearAllMarkers.value = true;
      lockMapUpdate.value = true;
      bandsValueChanged.value = false;
      lockZoom.value = true;
      //handleMapLockZoom();

      // Clear the map click event listener
      if (mapClickListener) {
        google.maps.event.removeListener(mapClickListener);
        mapClickListener = null; // Clear the reference
        mapClickEvent.value = false;
      }
    };

    const updateMap = () => {
      // Clear existing polyline and markers
      clearMapOverlays();

      if (baseMarker) {
        baseMarker.setMap(null); // Remove base marker
        baseMarker = null; // Reset baseMarker variable
      }

      if (remoteMarker) {
        remoteMarker.setMap(null); // Remove remote marker
        remoteMarker = null; // Reset remoteMarker variable
      }

      if (plottedBNMarkers.length > 0) {
        plottedBNMarkers.forEach((marker) => marker.setMap(null));
        plottedBNMarkers = [];
      }

      checkResultExist.value = false;

      if (sectorResults.sector_list || sectorResults.bn) {
        if (!lockMapUpdate.value) {
          activeBaseNode = null;
          activeBNIndex = -1;
        }

        checkResultExist.value = true;
        let baseNodeExist = false;
        let remoteNodeExist = false;

        let remoteNode = {};
        let baseNode = {};
        let remoteNodes = [];
        baseNodes = [];
        remoteNodeList = [];

        if (!multiSectorFlag.value) {
          if (sectorResults.bn) {
            baseNode.lat = sectorResults.bn.latitude
              ? parseFloat(sectorResults.bn.latitude)
              : "";
            baseNode.lng = sectorResults.bn.longitude
              ? parseFloat(sectorResults.bn.longitude)
              : "";
            const baseLatLng = new google.maps.LatLng(
              baseNode.lat,
              baseNode.lng
            );

            if (baseNode.lat != "" && baseNode.lng != "") {
              baseMarker = new google.maps.Marker({
                position: baseLatLng,
                map,
                icon: bnMarkerIcon,
                title: "BN Marker",
              });

              baseMarker.addListener("click", () => {
                showBaseNodeInfoWindow(sectorResults.bn, baseMarker);
              });

              baseNodeExist = true;
              selectedBN = sectorResults.bn;
            }
          }

          // Store all rn_list (Relay Nodes)
          if (sectorResults.rn_list && sectorResults.rn_list.length > 0) {
            const bestHeightData = selectBestHeight(sectorResults.rn_list);
            if (bestHeightData) {
              selectedRNList = sectorResults.rn_list;
              selectedRN = bestHeightData.remote;
              remoteNode.lat = parseFloat(selectedRN.rn.latitude);
              remoteNode.lng = parseFloat(selectedRN.rn.longitude);
              if (remoteNode.lat != "" && remoteNode.lng != "") {
                placeRemoteNodeMarker(remoteNode, baseNodeExist);
                remoteNodeExist = true;
              }
            }
          }
          if (baseNodeExist && remoteNodeExist) {
            drawSingleSectorPolyline(baseNode, remoteNode);
          } else {
            emit("update-results-modal");
            clearZoomAndMapClick();
          }
        } else {
          if (sectorResults.sector_list) {
            sectorResults.sector_list.forEach((sector) => {
              // Store Remote Nodes (RN)
              if (sector.rn_list && sector.rn_list.length > 0) {
                const bestHeightData = selectBestHeight(sector.rn_list);
                if (bestHeightData && bestHeightData.remote) {
                  // Store Base Node (BN)
                  if (
                    sector.bn &&
                    typeof sector.bn === "object" &&
                    Object.keys(sector.bn).length > 0
                  ) {
                    if (sector.bn.latitude && sector.bn.longitude) {
                      const baseLatLng = {
                        lat: parseFloat(sector.bn.latitude),
                        lng: parseFloat(sector.bn.longitude),
                      };

                      if (!isNaN(baseLatLng.lat) && !isNaN(baseLatLng.lng)) {
                        // Extract valid frequencies and convert them to GHz
                        let frequenciesGHz = sector.bn.carrier
                          .map((c) => Math.floor(c.frequency / 1000))
                          .filter((f) => f > 0)
                          .map((f) => f.toString());

                        // Check if at least one frequency is active
                        let hasActiveFrequency = frequenciesGHz.some((f) =>
                          activeBands.value.includes(f)
                        );

                        if (!hasActiveFrequency) {
                          if (
                            sector.bn &&
                            activeBaseNode == sector.bn &&
                            activeHeatmap.value &&
                            activeHeight.value
                          ) {
                            removeHeatmap(false);
                          }
                          return; // Hide this BN (skip further processing)
                        }

                        const bnMarker = new google.maps.Marker({
                          position: baseLatLng,
                          map,
                          icon: bnMarkerIcon,
                          title: "BN Marker",
                        });

                        bnMarker.addListener("click", () => {
                          showBaseNodeInfoWindow(sector.bn, bnMarker);
                        });

                        plottedBNMarkers.push(bnMarker);
                        baseNodes.push(sector.bn);
                        remoteNodeList.push(sector.rn_list);
                        remoteNodes.push(bestHeightData.remote);
                      }
                    }
                  }
                }
              }
            });

            if (baseNodes.length > 0) baseNodeExist = true;

            if (remoteNodes.length == 0) {
              remoteNodes.push(sectorResults.sector_list[0]?.rn_list[0]);
            }

            if (remoteNodes.length > 0) {
              remoteNode.lat = parseFloat(remoteNodes[0].rn.latitude);
              remoteNode.lng = parseFloat(remoteNodes[0].rn.longitude);

              if (remoteNode.lat != "" && remoteNode.lng != "") {
                placeRemoteNodeMarker(remoteNode, baseNodeExist);
                remoteNodeExist = true;
              }
            }
            if (baseNodeExist && remoteNodeExist) {
              drawMultiSectorPolyline(baseNodes, remoteNodes);
            } else {
              emit("update-results-modal");
              clearZoomAndMapClick();
            }
          }
        }
      }
    };

    const resizeMap = () => {
      if (mapDivRef.value && !checkResultExist.value) {
        mapDivRef.value.style.height = window.innerHeight - 40 + "px";
        getMapCenterAndRadius();
      }
    };

    return {
      mapDivRef,
      mapType,
      lockZoom,
      resetAll,
      resetAllValues,
      enableHeatmap,
      handleHeatmap,
      checkResultExist,
      loadingButton,
      heatmapLoading,
      handleZoomDragforBN,
      bandOptions,
      heatmapOptions,
      heightOptions,
      activeBands,
      activeHeatmap,
      activeHeight,
      legendUrl,
      setActiveBands,
      setActiveHeatmap,
      setActiveHeight,
      setMapType,
    };
  },
};
</script>
